import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Link } from '@uhg-abyss/web/ui/Link';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';

import { LANGUAGES } from '../../frontends/ProviderSearch/context/Internationalization/types';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGetProviderDirectoriesCNS } from '../../hooks/useGetProviderDirectoryCNSPdfs/useGetProviderDirectoriesCNS';
import { useGetProviderDirectories } from '../../hooks/useGetProviderDirectoryPdfs/useGetProviderDirectories';
import { Constants, LOB } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import {
  DocumentLinkContainer,
  DropDownContainer,
  ProviderDirectoryContentWrapped,
  ViewDocumentsContainer,
  YearBadge,
  YearContainer,
  providerDirectoryHeadingStyle,
  viewDocumentsTextStyle,
} from './ProviderDirectory.styled';

interface Document {
  name: string;
  language: string;
  year: string;
  link: string;
  docSize: string;
  docLastModifiedDate: string;
}

interface YearGroup {
  year: string;
  documents: Document[];
}

export const ProviderDirectory = () => {
  const [searchParams] = useSearchParams();
  const groupNumberFromUrl = searchParams?.get('groupNumber') || '';
  const [lob] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.LOB,
    ''
  );
  const [enableCnsDocUrlV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CNS_DOC_URL_V2,
  ]);
  const isCNS =
    lob?.toLowerCase() === LOB.CNS.toLowerCase() ||
    lob?.toLowerCase() === 'cns';
  const planSelectorVariables = getPlanSelectorVariables();
  const { accountId } = planSelectorVariables;
  const selectedGroupNumber = groupNumberFromUrl || '';
  const [, getProviderDirectories] = useGetProviderDirectories({
    onCompleted: (data: any) => {
      if (data?.data?.getProviderDirectory) {
        setProviderDirectoryList(data.data?.getProviderDirectory);
      }
    },
  });

  const [, getProviderDirectoriesCNS] = useGetProviderDirectoriesCNS({
    onCompleted: (data: any) => {
      if (data?.data?.getProviderDirectoryCNS) {
        setProviderDirectoryList(data.data?.getProviderDirectoryCNS);
      }
    },
  });

  const [providerDirectoryList, setProviderDirectoryList] = useState<
    YearGroup[]
  >([]);
  const variables = {
    planContextId: accountId || '',
    groupNumber: selectedGroupNumber,
  };

  useEffect(() => {
    if (accountId) {
      if (isCNS) {
        getProviderDirectoriesCNS({
          variables: {
            enableCnsDocUrlV2,
          },
        });
      } else {
        getProviderDirectories({ variables });
      }
    }
  }, [accountId, lob]);

  const { t, i18n } = useTranslation();
  const [filteredDocs, setFilteredDocs] = useState<YearGroup[]>([]);
  const [languageOptions, setLanguageOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const initializeSelectedLanguage = (uniqueLanguages: Set<string>) => {
    if (uniqueLanguages.size > 0) {
      if (uniqueLanguages.has('en')) {
        setSelectedLanguage('en');
      } else {
        setSelectedLanguage(Array.from(uniqueLanguages)[0]);
      }
    }
  };
  const tdoc = i18n.getFixedT(selectedLanguage);
  const getActualLocale = (localeStr: string) => {
    switch (localeStr) {
      case 'es':
      case 'es-us':
      case 'es_us':
      case 'es-US':
        return 'es';
      case 'en':
      case 'en_us':
      case 'en-us':
      case 'en-US':
        return 'en';
      case 'zh':
      case 'ZH':
        return 'zh';
      default:
        return 'en';
    }
  };
  useEffect(() => {
    const filteredList = providerDirectoryList
      .map((yearGroup: { year: string; documents: Document[] }) => ({
        year: yearGroup.year,
        documents: yearGroup.documents.filter(
          (doc) => getActualLocale(doc.language) === selectedLanguage
        ),
      }))
      .filter((yearGroup) => yearGroup.documents.length > 0);
    setFilteredDocs(filteredList);
  }, [providerDirectoryList, selectedLanguage]);

  const addLanguageCode = (
    uniqueLanguages: Set<string>,
    doc: Document
  ): void => {
    if (!doc.language) return;
    const code = Object.keys(LANGUAGES).find(
      (langCode) => langCode === getActualLocale(doc.language)
    );
    if (code) uniqueLanguages.add(code);
  };

  useEffect(() => {
    const uniqueLanguages = new Set<string>();
    const allDocs = providerDirectoryList.flatMap(
      (yearGroup) => yearGroup.documents
    );
    allDocs.forEach((doc) => addLanguageCode(uniqueLanguages, doc));

    const options = Array.from(uniqueLanguages).map((lang) => ({
      label: LANGUAGES[lang]?.title || lang,
      value: lang,
    }));
    setLanguageOptions(options);
    initializeSelectedLanguage(uniqueLanguages);
  }, [providerDirectoryList]);

  const getPDFName = (document: Document): string => {
    const docSize = document?.docSize || '';
    return (
      tdoc('PROVIDER_DIRECTORY.HEADING') +
      (docSize ? ` (PDF, ${docSize})` : '') +
      (document?.docLastModifiedDate
        ? ` ${tdoc('PROVIDER_DIRECTORY.UPDATED')}: ${
            document.docLastModifiedDate
          }`
        : '')
    );
  };

  return (
    <PageBody
      css={{
        'abyss-page-body-container': {
          backgroundColor: '$pageBodyBgColor',
          maxWidth: '100%',
          padding: 0,
        },
      }}
      id="main-content"
      tabIndex={-1}
    >
      <ProviderDirectoryContentWrapped>
        <Heading
          css={providerDirectoryHeadingStyle}
          data-testid="document-link"
          level={2}
          size="sm"
        >
          {t('PROVIDER_DIRECTORY.HEADING')}
        </Heading>
        <ViewDocumentsContainer>
          <Text color="#323333" css={viewDocumentsTextStyle}>
            {t('PROVIDER_DIRECTORY.VIEW_DOCUMENTS_IN')}
          </Text>
        </ViewDocumentsContainer>
        <DropDownContainer>
          <SelectInput
            label={t('PROVIDER_DIRECTORY.DOCUMENT_LANGUAGE')}
            onChange={(value: string) => {
              setSelectedLanguage(value);
            }}
            options={languageOptions}
            value={selectedLanguage}
            width={300}
          ></SelectInput>
        </DropDownContainer>
        {filteredDocs &&
          filteredDocs.length > 0 &&
          filteredDocs.map((yearDocs) => (
            <YearContainer key={yearDocs.year}>
              <YearBadge>{yearDocs.year}</YearBadge>
              {yearDocs.documents &&
                yearDocs.documents.length > 0 &&
                yearDocs.documents.map((document) => (
                  <DocumentLinkContainer key={document.name}>
                    <Link
                      href={document.link}
                      isStandardAnchor={true}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Text
                        color="$core.color.neutral.100"
                        fontSize="sm"
                        fontWeight="medium"
                      >
                        {getPDFName(document)}
                      </Text>
                    </Link>
                  </DocumentLinkContainer>
                ))}
            </YearContainer>
          ))}
      </ProviderDirectoryContentWrapped>
    </PageBody>
  );
};
